import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Home, ShoppingBag } from 'lucide-react';
import { orderService } from '@/services/order.service';
import { Order } from '@/types';
import { formatCurrency, formatDate } from '@/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/common/Skeleton';

export const OrderConfirmationPage = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderNumber) return;
    // Find order by orderNumber
    orderService.getUserOrders(1).then(res => {
      const found = res.data.data.find(o => o.orderNumber === orderNumber);
      if (found) setOrder(found);
    }).finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-16 space-y-4">
      <Skeleton className="h-24 w-24 rounded-full mx-auto" />
      <Skeleton className="h-8 w-2/3 mx-auto" />
      <Skeleton className="h-4 w-1/2 mx-auto" />
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      {/* Success Animation */}
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center animate-bounce">
          <CheckCircle className="h-12 w-12 text-success" />
        </div>
      </div>

      <h1 className="font-heading font-bold text-3xl text-gray-800 mb-2">Order Placed!</h1>
      <p className="text-gray-500 mb-6">
        Thank you for shopping with Milan Medical. Your order has been confirmed.
      </p>

      {order && (
        <div className="bg-white rounded-card shadow-card p-6 mb-8 text-left">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Order Number</p>
              <p className="font-heading font-bold text-lg text-primary-600">#{order.orderNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Total Paid</p>
              <p className="font-bold text-gray-800">{formatCurrency(Number(order.total))}</p>
            </div>
          </div>

          <div className="border-t border-gray-50 pt-4 space-y-3">
            {order.items?.slice(0, 3).map(item => (
              <div key={item.id} className="flex items-center gap-3">
                <img
                  src={item.product?.images?.[0] || '/placeholder-product.jpg'}
                  alt={item.product?.name}
                  className="w-12 h-12 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.product?.name}</p>
                  <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                </div>
                <p className="text-sm font-semibold text-gray-700">{formatCurrency(Number(item.totalPrice))}</p>
              </div>
            ))}
            {(order.items?.length || 0) > 3 && (
              <p className="text-xs text-gray-400 text-center">+{order.items.length - 3} more items</p>
            )}
          </div>

          <div className="border-t border-gray-50 pt-4 mt-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Package className="h-4 w-4 text-primary-500" />
              <span>Estimated delivery: {formatDate(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000))}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button asChild>
          <Link to="/orders">
            <Package className="h-4 w-4 mr-2" /> Track My Order
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/">
            <ShoppingBag className="h-4 w-4 mr-2" /> Continue Shopping
          </Link>
        </Button>
      </div>

      <div className="mt-8 p-4 bg-accent-50 rounded-xl">
        <p className="text-sm text-accent-800 font-medium">
          ⭐ You earned loyalty points on this order! Check your balance in <Link to="/profile/loyalty" className="underline font-semibold">Milan Points</Link>.
        </p>
      </div>
    </div>
  );
};
